import React, { useEffect, useRef, useState } from 'react';

function PricingInquiryModal({ option, onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const apiUrl = process.env.REACT_APP_CONTACT_API_URL
        || 'https://rnbcs16jm8.execute-api.ap-southeast-2.amazonaws.com/prod/contact';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'pricing',
          option,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: result.message
            || 'Request sent successfully! One of our Chief Trust Officers will be in contact shortly.',
        });
        setFormData({ name: '', email: '', phone: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || 'Failed to send request. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error submitting pricing inquiry:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="pricing-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pricing-modal-title"
      onClick={handleBackdropClick}
    >
      <div className={`pricing-modal pricing-modal--${option.toLowerCase()}`}>
        <button
          type="button"
          className="pricing-modal-close"
          onClick={onClose}
          aria-label="Close pricing inquiry"
        >
          &times;
        </button>

        <div className="pricing-modal-header">
          <span className="pricing-modal-eyebrow">Pricing Inquiry</span>
          <h2 id="pricing-modal-title">{option}</h2>
        </div>

        {submitStatus?.type === 'success' ? (
          <div className="pricing-modal-success">
            <div className="submit-status success">{submitStatus.message}</div>
            <button
              type="button"
              className="cta-primary pricing-modal-done"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        ) : (
          <form className="contact-form pricing-modal-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="pricing-name">Name *</label>
              <input
                ref={firstFieldRef}
                type="text"
                id="pricing-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="pricing-email">Email *</label>
              <input
                type="email"
                id="pricing-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="pricing-phone">Phone</label>
              <input
                type="tel"
                id="pricing-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>

            <button
              type="submit"
              className="cta-primary pricing-modal-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
            </button>

            {submitStatus?.type === 'error' && (
              <div className="submit-status error">{submitStatus.message}</div>
            )}

            <p className="form-note">
              One of our Chief Trust Officers will be in contact shortly to discuss your <strong>{option}</strong> inquiry.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default PricingInquiryModal;
